import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import type { KnowledgeProvider } from "@cloudox/api-client";
import { KnowledgeShell } from "@/components/layout/KnowledgeShell";
import { askSearch } from "@/lib/preview";
import { OverviewPage } from "@/pages/OverviewPage";
import { RelatedPagePage } from "@/pages/RelatedPagePage";
import { ViewPage } from "@/pages/ViewPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";

interface Props {
  readonly provider: KnowledgeProvider;
}

/**
 * The preview's route table, exported separately from {@link App} so tests can
 * mount it under a `MemoryRouter` and exercise real routes rather than pages in
 * isolation — the defects worth catching here (a rail entry pointing at a route
 * that does not exist, a section slug that resolves to a different section) live
 * between the routes, not inside one page.
 */
export function AppRoutes({ provider }: Props) {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route element={<KnowledgeShell provider={provider} />}>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/views/:view" element={<ViewPage />} />
        <Route path="/views/:view/:section" element={<ViewPage />} />
        <Route path="/pages/:page" element={<RelatedPagePage />} />
        <Route path="/assistant" element={<AssistantRedirect />} />
        <Route path="/assistant/:question" element={<AssistantRedirect />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * The Agent moved from a page to a panel over the overview; these keep the
 * shipped `/assistant/<question>` links working rather than 404ing them.
 */
function AssistantRedirect() {
  const { question } = useParams<{ question?: string }>();
  return <Navigate to={{ pathname: "/", search: askSearch(question) }} replace />;
}

/**
 * Public preview app — a Cloud Knowledge Workspace over a static, sanitized
 * demo environment. Knowledge Views are the navigation model; there is no run
 * or file browser.
 */
export function App({ provider }: Props) {
  return (
    <BrowserRouter>
      <AppRoutes provider={provider} />
    </BrowserRouter>
  );
}
